import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";

export default function PoliticaDeRambursare() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    document.title = "Politica de Rambursare — NIS2PASS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Politica de rambursare NIS2PASS - condiții și proceduri."
      );
    }
  }, []);

  return (
    <div className="lp-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --c-navy: #0F172A;
          --c-navy-dark: #1E293B;
          --c-navy-darkest: #0B1220;
          --c-blue: #2563EB;
          --c-blue-dark: #1D4ED8;
          --c-blue-light: #60A5FA;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-bg-light: #F8FAFC;
          --c-bg-page: #F7FAFF;
          --c-border-light: #E2E8F0;
          --c-border-mid: #CBD5E1;
          --c-text-body: #475569;
          --c-text-muted: #64748B;
          --c-text-dim: #94A3B8;
          --c-text-faint: #CBD5E1;
          --c-white: #FFF;
          --c-black: #111;
        }

        .lp-page { background: var(--c-bg-light); color: var(--c-navy); font-family: 'Sora', system-ui, sans-serif; min-height: 100vh; display: flex; flex-direction: column; }
        .lp-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .lp-page a { text-decoration: none; color: inherit; }

        /* NAV */
        .lp-nav { width: 100%; background: var(--c-navy); padding: 28px 0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lp-nav-inner { max-width: 1200px; width: 100%; padding: 0 24px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
        .lp-nav-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; text-decoration: none; }
        .lp-nav-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .lp-nav-brand { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-white); }
        .lp-nav-links { display: flex; align-items: center; gap: 24px; }
        .lp-nav-link { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .lp-nav-link:hover { color: var(--c-white); }
        .lp-nav-link.active { color: var(--c-blue-light); }
        .lp-nav-btns { display: flex; align-items: center; gap: 12px; }
        .lp-nav-ghost { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); background: transparent; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms; }
        .lp-nav-ghost:hover { border-color: var(--c-blue-light); color: var(--c-white); background: rgba(96,165,250,0.08); }
        .lp-nav-cta { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-black); background: var(--c-orange); border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms; }
        .lp-nav-cta:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* MAIN CONTENT */
        .lp-main { flex: 1; max-width: 1120px; width: 100%; margin: 0 auto; padding: 80px 24px; }

        /* HEADER */
        .lp-label { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-blue); letter-spacing: 1.4px; text-transform: uppercase; margin-bottom: 12px; }
        .lp-title { font-family: 'Sora', sans-serif; font-size: 38px; font-weight: 700; color: var(--c-navy); letter-spacing: -1.2px; line-height: 1.08; margin-bottom: 32px; }

        /* DOCUMENT CARD */
        .lp-card { background: var(--c-white); border: 1px solid var(--c-border-light); border-radius: 8px; padding: 32px 36px; position: relative; }

        .lp-doc-title { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; color: var(--c-navy); margin-bottom: 8px; }
        .lp-doc-date { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 4px; }
        .lp-doc-company { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 16px; }
        .lp-doc-divider { border: none; height: 1px; background: var(--c-border-light); margin: 16px 0 24px; }
        .lp-doc-heading { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--c-navy); margin: 24px 0 8px; }
        .lp-doc-body { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin-bottom: 8px; }
        .lp-doc-body strong { color: var(--c-navy); font-weight: 700; }
        .lp-doc-list { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin: 4px 0 12px 20px; list-style: disc; }
        .lp-doc-list li { margin-bottom: 4px; }
        .lp-doc-list li strong { color: var(--c-navy); font-weight: 700; }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .lp-main { padding: 48px 24px; }
          .lp-title { font-size: 28px; letter-spacing: -0.8px; margin-bottom: 24px; }
          .lp-card { padding: 24px 20px; }
          .lp-doc-title { font-size: 20px; }
          .lp-nav-links, .lp-nav-btns { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-logo">
            <span className="lp-nav-logo-mark" />
            <span className="lp-nav-brand">NIS2PASS</span>
          </a>
          <div className="lp-nav-links">
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/")}
            >
              Platforma
            </button>
            <button
              type="button"
              className="lp-nav-link active"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="lp-nav-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/logout")}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Intra in cont
                </button>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/auth")}
                >
                  Creeaza cont gratuit
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="lp-main">
        <p className="lp-label">DOCUMENTE LEGALE NIS2PASS</p>
        <h1 className="lp-title">Politica de Rambursare</h1>
        <div className="lp-card">
          <h2 className="lp-doc-title">
            Politica de Rambursare — Versiunea 1.0
          </h2>
          <p className="lp-doc-date">22 Septembrie 2025</p>
          <p className="lp-doc-company">
            Compliance Made Easy SRL · CUI: 48993456 · J2023019988400
          </p>
          <hr className="lp-doc-divider" />

          <h3 className="lp-doc-heading">1. Dispoziții Generale</h3>
          <p className="lp-doc-body">
            Prezenta politică reglementează condițiile de rambursare pentru
            serviciile achiziționate prin platforma NIS2PASS, operată de
            Compliance Made Easy SRL.
          </p>

          <h3 className="lp-doc-heading">2. Dreptul de Retragere</h3>
          <p className="lp-doc-body">
            În conformitate cu legislația română privind comerțul electronic și
            cu Directiva 2011/83/UE, utilizatorii au dreptul de a se retrage din
            contractul de achiziție în termen de 14 zile calendaristice de la
            data achiziției, fără a fi necesară justificarea deciziei.
          </p>

          <h3 className="lp-doc-heading">3. Condiții de Rambursare</h3>
          <p className="lp-doc-body">Rambursarea se acordă integral dacă:</p>
          <ul className="lp-doc-list">
            <li>
              Solicitarea este transmisă în termenul de 14 zile de la achiziție.
            </li>
            <li>
              Serviciul nu a fost utilizat substanțial (mai puțin de 20% din
              funcționalitățile premium accesate).
            </li>
            <li>Documentele generate prin platformă nu au fost descărcate.</li>
          </ul>

          <h3 className="lp-doc-heading">4. Excluderi de la Rambursare</h3>
          <p className="lp-doc-body">
            Nu se acordă rambursare în următoarele situații:
          </p>
          <ul className="lp-doc-list">
            <li>
              Creditele au fost consumate integral sau parțial peste pragul de
              20%.
            </li>
            <li>
              Au trecut mai mult de 14 zile calendaristice de la achiziție.
            </li>
            <li>
              Serviciul a fost utilizat pentru generarea și descărcarea
              documentelor oficiale.
            </li>
          </ul>

          <h3 className="lp-doc-heading">5. Procedura de Rambursare</h3>
          <p className="lp-doc-body">Pentru a solicita o rambursare:</p>
          <ul className="lp-doc-list">
            <li>
              Trimiteți un e-mail la contact@nis2pass.ro cu subiectul
              &quot;Solicitare rambursare&quot;.
            </li>
            <li>
              Includeți: numele complet, adresa de e-mail asociată contului,
              data achiziției, motivul solicitării.
            </li>
            <li>
              Rambursarea va fi procesată în maximum 14 zile lucrătoare de la
              aprobarea solicitării.
            </li>
            <li>
              Suma va fi returnată prin aceeași metodă de plată utilizată la
              achiziție.
            </li>
          </ul>

          <h3 className="lp-doc-heading">6. Contact</h3>
          <p className="lp-doc-body">
            Pentru orice întrebări legate de rambursări, contactați-ne la
            contact@nis2pass.ro.
          </p>
        </div>
      </main>

      {/* FOOTER */}
      <SharedFooter />
    </div>
  );
}
